import { useState } from "react";

import { Link } from "react-router-dom";
import { Heart, ShoppingBag, Star } from "lucide-react";
import ShareProductButton from "@/components/ShareProductButton";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/lib/api";

const FALLBACK_IMAGE = "/nityavastra-logo.png";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const absoluteImgUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

export default function ProductCard({ product }) {
  const { toggleWishlist, isInWishlist, addToCart } = useCart();
  const liked = isInWishlist(product.id);
  const variants = (product.variants || []).filter(v => !v.is_archived);
  const soldOut = Number(product.stock || 0) <= 0 ||
    (variants.length > 0 && !variants.some(v => Number(v.stock || 0) > 0));
  const [imageFailed, setImageFailed] = useState(false);
  const discount = product.old_price
    ? Math.round(((product.old_price - product.price) / product.old_price) * 100)
    : 0;

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="nv-modern-product-card group relative overflow-hidden bg-white"
    >
      <Link to={`/product/${product.slug || product.id}`} className="block">
        <div className="relative aspect-[3/4] overflow-hidden bg-[linear-gradient(145deg,#F1FAF7_0%,#FFFFFF_52%,#FFF2F6_100%)]">
          <div className="absolute inset-x-0 top-0 z-[1] h-1 bg-[linear-gradient(90deg,#0F8F83_0%,#22B8A7_30%,#F0B940_55%,#FF6B5F_76%,#D84C8B_100%)]" />
          <img
            src={imageFailed ? FALLBACK_IMAGE : absoluteImgUrl(product.image)}
            alt={imageFailed ? "Nityavastra" : product.name}
            loading="lazy"
            onError={() => setImageFailed(true)}
            className={`h-full w-full transition-transform duration-700 ease-out group-hover:scale-[1.025] ${imageFailed ? "object-contain p-10 opacity-70" : "object-contain"}`}
          />
          {soldOut && (
            <div className="absolute inset-x-0 bottom-0 z-10 bg-[#7C1F30] px-3 py-2 text-center text-xs font-semibold uppercase tracking-[0.18em] text-[#FAF3E7]">
              Sold Out
            </div>
          )}
          {discount > 0 && (
            <div className="absolute left-2 top-3 z-10 rounded-full bg-[linear-gradient(90deg,#FF6B5F,#D84C8B)] px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-white shadow-lg shadow-[#D84C8B]/15 sm:left-3 sm:text-[10px]">
              -{discount}%
            </div>
          )}
          <div className="absolute top-2 right-2 sm:top-3 sm:right-3 flex items-center gap-1.5 sm:gap-2">
            <ShareProductButton
              product={product}
              data-testid={`share-btn-${product.id}`}
              className="flex h-8 w-8 items-center justify-center rounded-full border border-white/80 bg-white/90 text-[#0F6A66] shadow-md backdrop-blur-sm transition-all hover:scale-105 hover:bg-[#EAF9F5] sm:h-9 sm:w-9"
            />

            <button
              type="button"
              data-testid={`wishlist-btn-${product.id}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleWishlist(product.id);
              }}
              className="flex h-8 w-8 items-center justify-center rounded-full border border-white/80 bg-white/90 shadow-md backdrop-blur-sm transition-all hover:scale-105 hover:bg-[#FFF0F5] sm:h-9 sm:w-9"
              aria-label="Toggle wishlist"
            >
              <Heart
                className={`h-4 w-4 transition-colors ${liked ? "fill-[#D84C8B] text-[#D84C8B]" : "text-[#315956]"}`}
              />
            </button>
          </div>
        </div>
      </Link>

      <div className="space-y-1 px-1 pb-2 pt-2 sm:px-1.5 sm:pb-2.5 sm:pt-2">
        <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#0F8F83] sm:text-[10px] sm:tracking-[0.2em]">
          {product.category.replace("-", " ")}
        </p>
        <Link to={`/product/${product.slug || product.id}`}>
          <h3 className="line-clamp-2 font-serif text-base leading-snug text-[#173A3A] transition-colors group-hover:text-[#D84C8B] sm:text-lg">
            {product.name}
          </h3>
        </Link>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <Star className="h-3 w-3 fill-[#D4A03A] text-[#D4A03A]" />
            <span className="text-xs font-medium text-[#536C69]">{product.rating || 4.5}</span>
          </div>
          <span className="text-xs text-[#81918F]">· {product.review_count || 0} reviews</span>
        </div>
        <div className="flex items-baseline gap-2 pt-1">
          <span className="text-lg font-bold text-[#0B6662] sm:text-xl">
            {formatINR(product.price)}
          </span>
          {product.old_price && (
            <span className="text-xs text-[#98A5A3] line-through sm:text-sm">
              {formatINR(product.old_price)}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            if (soldOut) return;
            addToCart(product.id, 1);
          }}
          disabled={soldOut}
          aria-disabled={soldOut}
          data-testid={`quick-add-btn-${product.id}`}
          className="mt-2 flex min-h-10 w-full items-center justify-center gap-2 rounded-lg bg-[#F4FBF9] py-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#0F6A66] shadow-sm transition-all hover:border-transparent hover:bg-[linear-gradient(90deg,#0F7772,#11988C,#FF6B5F)] hover:text-white hover:shadow-lg sm:py-3 sm:text-xs sm:tracking-[0.18em]"
        >
          <ShoppingBag className="h-3.5 w-3.5" />
          {soldOut ? "Sold Out" : "Add to Cart"}
        </button>
      </div>
    </div>
  );
}
